"use client"

import {useState, useEffect, useRef, useCallback} from "react"
import {CircleHelp} from "lucide-react"
import {Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip"
import Chart from "react-apexcharts"
import {cn} from "@/lib/utils"
import axiosInstance from "@/api/axios"
import { Download , ChevronDown} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";

export function FillLevelConsistency({
    className, 
    title, 
    timeRange, 
    bins = [], 
    customDateRange = [{ startDate: null, endDate: null, key: 'selection' }],
    isCustomDateRange = false,
    ...props
}) {
    const { t } = useTranslation();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const exportRef = useRef(null);
    const [data, setData] = useState([])
    const dataRef = useRef([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const chartRef = useRef(null)
    const [chartKey, setChartKey] = useState(0)
    const [isRealData, setIsRealData] = useState(false)

    useEffect(() => {
        const isDark = document.documentElement.classList.contains('dark');
        setIsDarkMode(isDark);

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    const isNowDark = document.documentElement.classList.contains('dark');
                    setIsDarkMode(isNowDark);
                }
            });
        });

        observer.observe(document.documentElement, { attributes: true });

        return () => observer.disconnect();
    }, []);

    const [state, setState] = useState({
        series: [
            {
                name: "Fill Level Trends",
                data: [],
            },
        ],
        options: {
            chart: {
                type: "candlestick",
                height: 350,
                background: "transparent",
                toolbar: {
                    show: true,
                    tools: {
                        download: false,
                        selection: true,
                        zoom: true,
                        zoomin: true,
                        zoomout: true,
                        pan: true,
                        reset: true,
                    },
                    autoSelected: "zoom",
                },
                animations: {
                    enabled: false,
                },
                zoom: {
                    enabled: true,
                    type: "xy",
                    autoScaleYaxis: true,
                    zoomedArea: {
                        fill: {
                            color: "#90CAF9",
                            opacity: 0.4,
                        },
                        stroke: {
                            color: "#0D47A1",
                            opacity: 0.4,
                            width: 1,
                        },
                    },
                },
                selection: {
                    enabled: true,
                },
                pan: {
                    enabled: true,
                    type: "xy",
                },
            },
            title: {
                align: "left",
                style: {
                    fontSize: "14px",
                    fontFamily: "inherit",
                    color: "var(--foreground)",
                },
            },
            xaxis: {
                type: "category",
                labels: {
                    style: {
                        colors: "hsl(var(--foreground))",
                        fontFamily: "inherit",
                    },
                },
            },
            yaxis: {
                title: {
                    text: t('fillLevelConsistency.yAxisTitle'),
                    style: {
                        color: "hsl(var(--foreground))",
                    },
                },
                labels: {
                    style: {
                        colors: "hsl(var(--foreground))",
                        fontFamily: "inherit",
                    },
                    formatter: (value) => (value ? value.toFixed(1) + "%" : "0%"),
                },
            },
            plotOptions: {
                candlestick: {
                    colors: {
                        upward: "hsl(var(--fill-level-brown))",
                        downward: "hsl(var(--chart-6))",
                    },
                    wick: {
                        useFillColor: true,
                    },
                },
            },
            tooltip: {
                enabled: true,
                theme: isDarkMode ? "dark" : "light",
                shared: false,
                intersect: true,
                custom: ({series, seriesIndex, dataPointIndex, w}) => {
                    try {
                        const originalData = dataRef.current[dataPointIndex]
                        const chartData = w.globals.initialSeries[seriesIndex].data[dataPointIndex]

                        if (!originalData || !chartData || !originalData.y || originalData.y.every((val) => val === null || val === 0)) {
                            return `
                                <div class="apexcharts-tooltip-box" style="padding: 8px; background: white; border: 1px solid #e2e8f0; border-radius: 4px;">
                                    <div style="font-weight: bold; margin-bottom: 4px;">${chartData?.x || 'Unknown Period'}</div>
                                    <div>No data available for this period</div>
                                </div>
                            `
                        }

                        const [open, high, low, close] = originalData.y
                        const timeRangeLabel = getTimeRangePeriodLabel(originalData.x)

                        let trend = 'No data'
                        let trendColor = "#6b7280"
                        let trendIcon = "➖"

                        if (originalData.trend === "increasing") {
                            trend = '🟤 Waste accumulation'
                            trendColor = "hsl(var(--fill-level-brown))"
                            trendIcon = "📈"
                        } else if (originalData.trend === "decreasing") {
                            trend = '🟢 Waste removal'
                            trendColor = "hsl(var(--chart-6))"
                            trendIcon = "📉"
                        } else if (originalData.trend === "stable") {
                            if (originalData.current_median === 0 && originalData.previous_median === 0) {
                                trend = 'No data'
                                trendColor = "#6b7280"
                                trendIcon = "➖"
                            } else {
                                trend = '🟢 Waste removal'
                                trendColor = "hsl(var(--chart-6))"
                                trendIcon = "📉"
                            }
                        }

                        return `
                            <div class="apexcharts-tooltip-box" style="padding: 8px; background: white; border: 1px solid #e2e8f0; border-radius: 4px; min-width: 250px;">
                                <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">${timeRangeLabel}</div>
                                
                                <div style="display: grid; grid-template-columns: auto auto; gap: 8px 12px; margin-bottom: 8px;">
                                    <div style="color: #6b7280;">Previous Period:</div>
                                    <div style="font-weight: 500;">${originalData.previous_median !== null && originalData.previous_median !== undefined ? originalData.previous_median.toFixed(1) + "%" : "0%"}</div>
                                    
                                    <div style="color: #6b7280;">Current Period:</div>
                                    <div style="font-weight: 500;">${originalData.current_median !== null && originalData.current_median !== undefined ? originalData.current_median.toFixed(1) + "%" : "0%"}</div>
                                    
                                    <div style="color: #6b7280;">Next Period:</div>
                                    <div style="font-weight: 500;">${originalData.q3 !== null && originalData.q3 !== undefined ? originalData.q3.toFixed(1) + "%" : "0%"}</div>
                                </div>
                                
                                <div style="border-top: 1px solid #e2e8f0; padding-top: 8px;">
                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <span style="font-size: 16px;">${trendIcon}</span>
                                        <span style="color: ${trendColor}; font-weight: bold;">${trend}</span>
                                    </div>
                                </div>
                            </div>
                        `
                    } catch (error) {
                        console.error("Error rendering tooltip:", error)
                        return `
                            <div class="apexcharts-tooltip-box" style="padding: 8px; background: white; border: 1px solid #e2e8f0; border-radius: 4px;">
                                <div style="font-weight: bold; margin-bottom: 4px;">Error</div>
                                <div>Unable to display data</div>
                            </div>
                        `
                    }
                },
            },
            states: {
                hover: {
                    filter: {
                        type: "none",
                    },
                },
                active: {
                    filter: {
                        type: "none",
                    },
                    allowMultipleDataPointsSelection: false,
                },
                normal: {
                    filter: {
                        type: "none",
                    },
                },
            },
            responsive: [
                {
                    breakpoint: 9999,
                    options: {
                        chart: {
                            width: "100%",
                        },
                    },
                },
            ],
            noData: {
                text: t('fillLevelConsistency.noDataAvailable'),
                align: "center",
                verticalAlign: "middle",
                offsetX: 0,
                offsetY: 0,
                style: {
                    color: "var(--muted-foreground)",
                    fontSize: "14px",
                    fontFamily: "inherit",
                },
            },
        },
    })

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            options: {
                ...prevState.options,
                yaxis: {
                    ...prevState.options.yaxis,
                    title: {
                        ...prevState.options.yaxis.title,
                        text: t('fillLevelConsistency.yAxisTitle'),
                    },
                },
                noData: {
                    ...prevState.options.noData,
                    text: t('fillLevelConsistency.noDataAvailable'),
                },
            },
        }));
    }, [t]);

    useEffect(() => {
        if (!timeRange && !isCustomDateRange) return;

        setIsRealData(false);
        setData([]);
        dataRef.current = [];
        
        fetchRealData();
    }, [timeRange, bins, customDateRange, isCustomDateRange]);

    const fetchRealData = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                bins: bins.join(",")
            };

            if (isCustomDateRange && customDateRange[0] && customDateRange[0].startDate && customDateRange[0].endDate) {
                const startDate = customDateRange[0].startDate;
                const endDate = customDateRange[0].endDate;
                
                if (startDate instanceof Date) {
                    params.start_date = startDate.toISOString();
                } else if (typeof startDate === 'string') {
                    params.start_date = startDate;
                } else {
                    params.start_date = new Date(startDate).toISOString();
                }
                
                if (endDate instanceof Date) {
                    params.end_date = endDate.toISOString();
                } else if (typeof endDate === 'string') {
                    params.end_date = endDate;
                } else {
                    params.end_date = new Date(endDate).toISOString();
                }
                
                console.log('FillLevelConsistency: Using custom date range', params.start_date, params.end_date);
            } else {
                params.timeRange = timeRange;
                console.log('FillLevelConsistency: Using time range', timeRange);
            }

            console.log("FillLevelConsistency: Fetching data with params:", params);

            const response = await axiosInstance.get("fill_level_consistency", {
                params: params,
                paramsSerializer: {
                    indexes: null,
                },
            });

            console.log("FillLevelConsistency: API response:", response.data);

            if (response.data && Array.isArray(response.data)) {
                const processedData = response.data.map(item => ({
                    ...item,
                    y: item.y ? item.y.map(val => val === null ? 0 : val) : [0, 0, 0, 0],
                    current_median: item.current_median !== null ? item.current_median : 0,
                    previous_median: item.previous_median !== null ? item.previous_median : 0,
                    q1: item.q1 !== null ? item.q1 : 0,
                    q3: item.q3 !== null ? item.q3 : 0,
                    min: item.min !== null ? item.min : 0,
                    max: item.max !== null ? item.max : 0,
                }));
                
                setData(processedData);
                dataRef.current = processedData;
                transformDataForMedianTrendCandlestick(processedData);
                setIsRealData(true);
            } else {
                console.error("Invalid data format received from API");
                setError(t('fillLevelConsistency.errors.invalidDataFormat'));
            }
        } catch (error) {
            console.error("Error fetching fill level consistency data:", error);
            setError(error.message || t('fillLevelConsistency.errors.failedToFetchData'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setChartKey((prev) => prev + 1)

        return () => {
            if (chartRef.current) {
                try {
                    setTimeout(() => {
                        if (typeof window !== "undefined") {
                            window.dispatchEvent(new Event("resize"))
                        }
                    }, 100)
                } catch (error) {
                    console.warn("Chart cleanup warning:", error)
                }
            }
        }
    }, [timeRange, bins])

    const transformDataForMedianTrendCandlestick = (apiData) => {
        try {
            console.log("Raw API data for transformation:", apiData)

            if (!apiData || apiData.length === 0) {
                setState((prevState) => ({
                    ...prevState,
                    series: [
                        {
                            name: t('fillLevelConsistency.seriesName'),
                            data: [],
                        },
                    ],
                }))
                return
            }

            const candlestickData = apiData
                .map((item) => {
                    if (!item.y || !Array.isArray(item.y)) {
                        return {
                            x: item.x,
                            y: [0, 0, 0, 0],
                        }
                    }

                    return {
                        x: item.x,
                        y: item.y.map(val => val === null ? 0 : val),
                    }
                })

            console.log("Transformed median trend candlestick data:", candlestickData)
            console.log("Original data stored in state:", apiData)

            setState((prevState) => ({
                ...prevState,
                series: [
                    {
                        name: t('fillLevelConsistency.seriesName'),
                        data: candlestickData,
                    },
                ],
            }))
        } catch (error) {
            console.error("Error transforming chart data:", error)
            setError(t('fillLevelConsistency.errors.failedToProcessData'))
        }
    }

    const getTimeRangePeriodLabel = (periodKey) => {
        if (!periodKey) return t('fillLevelConsistency.tooltip.unknown')

        if (timeRange === "Day") {
            return `${periodKey}`
        } else if (timeRange === "Week") {
            return periodKey
        } else if (timeRange === "Month") {
            return t('fillLevelConsistency.weekOf', { week: periodKey })
        } else if (timeRange === "3 Months" || timeRange === "6 Months") {
            return periodKey
        } else {
            return periodKey
        }
    }

    const renderContent = () => {
        if (loading && !data.length) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-muted-foreground">{t('fillLevelConsistency.loading')}</div>
                </div>
            )
        }

        if (error && !data.length) {
            return (
                <div className="flex items-center justify-center h-full">
                    <div className="text-red-500">{t('fillLevelConsistency.error')}: {error}</div>
                </div>
            )
        }

        return (
            <div className="h-full w-full flex flex-col">
                <div className="flex-1 relative" ref={chartRef}>
                    {state.series[0].data.length > 0 ? (
                        <Chart
                            key={`fill-level-trend-chart-${chartKey}`}
                            options={state.options}
                            series={state.series}
                            type="candlestick"
                            height="100%"
                            width="100%"
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-muted-foreground">{t('fillLevelConsistency.noDataForSelection')}</div>
                        </div>
                    )}
                </div>

                {state.series[0].data.length > 0 && (
                    <div className="text-center mt-2">
                        <div className="text-xs text-muted-foreground inline-block bg-background/80 px-2 py-1 rounded-md">
                            <span>🟤 Waste accumulation | 🟢 Waste removal</span>
                            {!isRealData && data.length > 0 && (
                                <div className="text-xs text-amber-500 mt-0.5">
                                    {t('fillLevelConsistency.previewData')}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const convertToCSV = () => {
        if (!data || data.length === 0) return "";

        const headers = [
            'Period',
            'Previous Period',
            'Current Period',
            'Next Period',
            'Trend'
        ];

        const rows = data.map(item => {
            let trendValue = "No data";
            if (item.trend === "increasing") {
                trendValue = "Waste accumulation";
            } else if (item.trend === "decreasing") {
                trendValue = "Waste removal";
            } else if (item.trend === "stable") {
                if (item.current_median === 0 && item.previous_median === 0) {
                    trendValue = "No data";
                } else {
                    trendValue = "Waste removal";
                }
            }
            
            return [
                item.x,
                (item.previous_median !== null && item.previous_median !== undefined ? item.previous_median.toFixed(1) + "%" : "0%"),
                (item.current_median !== null && item.current_median !== undefined ? item.current_median.toFixed(1) + "%" : "0%"),
                (item.q3 !== null && item.q3 !== undefined ? item.q3.toFixed(1) + "%" : "0%"),
                trendValue
            ].join(",");
        });

        return [headers.join(","), ...rows].join("\n");
    };

    const handleExportCSV = useCallback(() => {
        if (data.length === 0) return

        const csvContent = convertToCSV()
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.setAttribute("href", url)
        link.setAttribute("download", `fill_level_trend_${new Date().toISOString().slice(0, 10)}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }, [convertToCSV, data])

    const getExportData = useCallback(() => {
        if (!data || data.length === 0) return null

        const headers = [
            'Period',
            'Previous Period',
            'Current Period',
            'Next Period',
            'Trend'
        ]
        
        const rows = data.map(item => {
            let trendValue = "No data";
            if (item.trend === "increasing") {
                trendValue = "Waste accumulation";
            } else if (item.trend === "decreasing") {
                trendValue = "Waste removal";
            } else if (item.trend === "stable") {
                if (item.current_median === 0 && item.previous_median === 0) {
                    trendValue = "No data";
                } else {
                    trendValue = "Waste removal";
                }
            }
            
            return [
                item.x,
                (item.y?.[0] !== null && item.y?.[0] !== undefined ? item.y[0].toFixed(1) + "%" : "0%"),
                (item.y?.[3] !== null && item.y?.[3] !== undefined ? item.y[3].toFixed(1) + "%" : "0%"),
                (item.y?.[1] !== null && item.y?.[1] !== undefined ? item.y[1].toFixed(1) + "%" : "0%"),
                trendValue
            ];
        })

        return {
            title: title || t('fillLevelConsistency.title'),
            headers,
            rows,
            chartData: data.map(item => ({
                x: item.x,
                y: item.y?.[3] || 0
            }))
        }
    }, [data, title, t])

    const handleExportPNG = async () => {
        if (!exportRef.current) return;

        try {
            const elementsToHide = exportRef.current.querySelectorAll('.fill-level-export, .no-export');
            elementsToHide.forEach(el => {
                el.style.visibility = 'hidden';
            });

            await new Promise(resolve => setTimeout(resolve, 200));

            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(exportRef.current, {
                scale: 2,
                logging: true,
                useCORS: true,
                backgroundColor: "#FFFFFF",
                ignoreElements: (el) => el.classList.contains('no-export'),
                onclone: (clonedDoc) => {
                    const chartContainer = clonedDoc.querySelector('.apexcharts-canvas');
                    if (chartContainer) {
                        chartContainer.style.opacity = '1';
                        chartContainer.style.visibility = 'visible';
                    }
                }
            });

            const link = document.createElement('a');
            link.download = `fill_level_trend_${timeRange || 'data'}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Failed to export PNG:', err);
        } finally {
            const elementsToHide = exportRef.current.querySelectorAll('.no-export');
            elementsToHide.forEach(el => {
                el.style.visibility = '';
            });
        }
    };

    return (
        <div
            ref={exportRef}
            className={cn("exportable-section lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4", className)}
            data-export-data={JSON.stringify(getExportData())}
            {...props}
        >
            <div className="relative">
                <div className="flex justify-between items-center mb-2">
                    <div>
                        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                            {title || t('fillLevelConsistency.defaultTitle')}
                        </h4>
                        {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
                            <p className="text-sm text-muted-foreground mt-1">
                                {new Date(customDateRange[0].startDate).toLocaleDateString()} - {new Date(customDateRange[0].endDate).toLocaleDateString()}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center space-x-2 export-controls fill-level-export no-export">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground hover:text-primary"
                                    disabled={data.length === 0}
                                >
                                    <Download className="h-4 w-4 mr-1"/>
                                    {t('fillLevelConsistency.export.export')}
                                    <ChevronDown className="h-4 w-4 ml-1"/>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onClick={handleExportCSV}
                                    disabled={data.length === 0}
                                >
                                    {t('fillLevelConsistency.export.downloadCSV')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={handleExportPNG}
                                    disabled={data.length === 0}
                                >
                                    {t('fillLevelConsistency.export.downloadPNG')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <TooltipProvider>
                            <UITooltip>
                                <TooltipTrigger asChild>
                                    <CircleHelp className="h-5 w-5 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                    <p dangerouslySetInnerHTML={{
                                        __html: t('fillLevelConsistency.help.description') + '<br/><br/><strong>Date Range:</strong> Use the calendar in the header to select a custom date range'
                                    }} />
                                </TooltipContent>
                            </UITooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </div>

            <div className="px-2 pt-4 sm:px-6 sm:pt-6" style={{ width: '100%', height: '100%' }}>
                {renderContent()}
            </div>
        </div>
    )
}